import * as Sentry from '@sentry/react';
import { createFileRoute, invariant, redirect } from '@tanstack/react-router';
import jwtDecode from 'jwt-decode';
import { queryEntepriseOrgSettings } from 'src/api/queries/enterpriseAdmin';

import { queryEnterpriseEnrollmentStatus } from 'src/api/queries/enterpriseEnrollment';
import type { RmClaims } from 'src/api/queries/user';
import { enterpriseHelpers } from 'src/utils/enterpriseHelpers';
import { AccessDeniedError } from 'src/utils/errors';
import { LogCategory } from 'src/utils/logger.categories';
import { userHelpers } from 'src/utils/userHelpers';

export const Route = createFileRoute('/_auth/enterprise/_with-org')({
  async beforeLoad({ context, location }) {
    const auth = await context.auth;
    const accessTokenClaims: RmClaims = await jwtDecode(
      await auth.getAccessTokenSilently()
    );

    invariant(auth.user, 'User not found');

    if (!userHelpers.isOrganizationMember(auth.user)) {
      Sentry.addBreadcrumb({
        category: LogCategory.Auth,
        message: '_with-org: Access Denied: User is not an organization member',
      });

      throw new AccessDeniedError(
        'Only users in an organization can access this page.'
      );
    }

    if (userHelpers.hasActiveEnterpriseEnrollment(auth.user)) {
      // We can't fully trust the ID Token to be up to date, so we need to
      // double check the enrollment status with the server.
      const enrollmentStatus = await context.queryClient.ensureQueryData(
        queryEnterpriseEnrollmentStatus()
      );

      const doesNotHaveActiveEnrollment =
        !enrollmentStatus ||
        enrollmentStatus.enrollment === 'FINALIZED' ||
        enrollmentStatus.enrollment === 'CANCELLED';

      if (doesNotHaveActiveEnrollment) {
        Sentry.addBreadcrumb({
          category: LogCategory.Auth,
          message:
            'User has active enterprise enrollment claim, but no active enrollment found, allowing access',
        });
      } else {
        Sentry.addBreadcrumb({
          category: LogCategory.Auth,
          message:
            'User has active enterprise enrollment, redirecting to enroll',
        });
        throw redirect({
          from: '/enterprise',
          to: '/enterprise/enroll',
          replace: true,
        });
      }
    }

    if (userHelpers.hasPermissionReadOrganizationSettings(accessTokenClaims)) {
      const orgSettings = await context.queryClient.ensureQueryData(
        queryEntepriseOrgSettings()
      );

      if (enterpriseHelpers.hasStartedOffboarding(orgSettings)) {
        if (!userHelpers.hasPermissionCreateOffboarding(accessTokenClaims)) {
          Sentry.addBreadcrumb({
            category: LogCategory.Auth,
            message:
              '_with-org: Access Denied: Organization is offboarding, user lacks permission to view offboarding page',
          });
          throw new AccessDeniedError(
            'Your organization is currently offboarding. Please contact your administrator for more information.'
          );
        }

        if (!location.pathname.includes('/enterprise/offboarding')) {
          Sentry.addBreadcrumb({
            category: LogCategory.Auth,
            message:
              '_with-org: Access Denied: Organization is offboarding, redirecting to offboarding page',
          });
          throw redirect({
            from: '/enterprise',
            to: '/enterprise/offboarding',
            replace: true,
          });
        }
      }
    }
  },
});
